import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Mail, AlertCircle } from 'lucide-react'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import type { CreateImapAccountInput, EmailAccountDto } from '@jr2os/shared'
import { EMAIL_PRESETS as PRESETS } from './presets'

interface Props {
  onClose: () => void
  onCreated: (account: EmailAccountDto) => void
}

export default function NewAccountModal({ onClose, onCreated }: Props) {
  const { t } = useTranslation(['email', 'common'])
  const [presetIndex, setPresetIndex] = useState(0)
  const preset = PRESETS[presetIndex]!
  const [emailAddress, setEmailAddress] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [imapHost, setImapHost] = useState(preset.imapHost)
  const [imapPort, setImapPort] = useState(String(preset.imapPort))
  const [initialSyncDays, setInitialSyncDays] = useState('90')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onPickPreset = (idx: number) => {
    const p = PRESETS[idx]!
    setPresetIndex(idx)
    setImapHost(p.imapHost)
    setImapPort(String(p.imapPort))
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload: CreateImapAccountInput = {
        emailAddress,
        displayName: displayName || null,
        imapHost,
        imapPort: Number(imapPort),
        imapUseTls: preset.imapUseTls,
        smtpHost: preset.smtpHost || null,
        smtpPort: preset.smtpHost ? preset.smtpPort : null,
        smtpUseTls: preset.smtpUseTls,
        password,
        initialSyncDays: Number(initialSyncDays),
      }
      const { account } = await api.post<{ account: EmailAccountDto }>(
        '/email-accounts/imap',
        payload
      )
      onCreated(account)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(t('newAccountModal.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60'

  return (
    <Modal open onClose={onClose} title={t('newAccountModal.title')} maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('newAccountModal.fields.provider')}</label>
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map((p, i) => (
              <button
                key={p.labelKey}
                type="button"
                onClick={() => onPickPreset(i)}
                className={[
                  'px-3 py-2 rounded-lg text-sm border transition text-left',
                  i === presetIndex
                    ? 'bg-jr2-500/15 border-jr2-500/40 text-jr2-700 dark:text-jr2-200'
                    : 'bg-[var(--bg-input-soft)] border-[var(--border)] t-secondary hover:bg-[var(--bg-input)]',
                ].join(' ')}
              >
                {t(p.labelKey)}
              </button>
            ))}
          </div>
          {preset.hintKey && (
            <p className="mt-2 text-xs t-muted flex items-start gap-1.5">
              <AlertCircle size={12} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300" />
              {t(preset.hintKey)}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">{t('newAccountModal.fields.email')}</label>
            <input
              type="email"
              required
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder={t('newAccountModal.placeholders.email')}
              className={inputCls}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">{t('newAccountModal.fields.displayName')}</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('newAccountModal.placeholders.displayName')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('newAccountModal.fields.imapHost')}</label>
            <input
              required
              value={imapHost}
              onChange={(e) => setImapHost(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('newAccountModal.fields.port')}</label>
            <input
              type="number"
              required
              value={imapPort}
              onChange={(e) => setImapPort(e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs t-muted mb-1.5">{t('newAccountModal.fields.password')}</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('newAccountModal.fields.initialSyncDays')}</label>
            <input
              type="number"
              min={1}
              max={3650}
              value={initialSyncDays}
              onChange={(e) => setInitialSyncDays(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <p className="text-xs t-faint flex items-start gap-1.5">
          <Mail size={12} className="mt-0.5 shrink-0" />
          {t('newAccountModal.securityNote')}
        </p>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {t('newAccountModal.actions.connect')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
